// Visibility change hook — locks app when tab is hidden beyond grace period

import { useEffect, useRef } from 'react';
import { useLockStore } from '@/stores/lockStore';

export function useVisibilityLock() {
  const { isLocked, pinEnabled, preferences, lock } = useLockStore();
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!pinEnabled || isLocked || !preferences.lockOnHide) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden — start grace timer
        graceTimerRef.current = setTimeout(() => {
          lock();
        }, preferences.lockOnHideGraceSeconds * 1000);
      } else {
        // Tab visible again — cancel grace timer
        if (graceTimerRef.current) {
          clearTimeout(graceTimerRef.current);
          graceTimerRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
      }
    };
  }, [pinEnabled, isLocked, preferences.lockOnHide, preferences.lockOnHideGraceSeconds, lock]);
}
